import { ColumnDef } from "@tanstack/react-table";
import { ChevronDown, MoreHorizontal } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../../components/ui/alert-dialog";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { DataTable } from "../../components/ui/data-table";
import DataTableSearch from "../../components/ui/data-table/data-table-search";
import { useDeleteProfil } from "../../hooks/api/profil";
import { Profil } from "../../lib/api/types/profil";

interface ProfilListProps {
  profils: Profil[];
  isLoading: boolean;
  error: Error | null;
  onView: (id: number) => void;
  onEdit: (id: number) => void;
  page: number;
  limit: number;
  total: number;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  onSearchChange: (term: string) => void;
  searchTerm: string;
}

export function ProfilList({
  profils,
  isLoading,
  error,
  onView,
  onEdit,
  page,
  limit,
  total,
  onPageChange,
  onLimitChange,
  onSearchChange,
  searchTerm = "",
}: ProfilListProps) {
  const deleteProfil = useDeleteProfil();
  const [profilToDelete, setProfilToDelete] = useState<number | null>(null);

  const handleConfirmDelete = () => {
    if (profilToDelete) {
      deleteProfil.mutate(profilToDelete, {
        onSuccess: () => {
          setProfilToDelete(null);
        },
        onError: (err: unknown) => {
          const message = getApiErrorMessage(err);
          toast.error("Suppression impossible", { description: message });
        },
      });
    }
  };

  // Define columns for the DataTable
  const columns: ColumnDef<Profil>[] = [
    {
      accessorKey: "name",
      header: "Profil",
      cell: ({ row }) => <span className="font-medium">{row.original.name || "-"}</span>,
    },
    {
      accessorKey: "droit",
      header: "Droit",
      cell: ({ row }) => <span className="text-muted-foreground">{row.original.droit?.name || "Aucun droit associé"}</span>,
    },
    {
      accessorKey: "status",
      header: "Statut",
      cell: ({ row }) => {
        const profil = row.original;
        if (profil.deleted) {
          return <Badge variant="destructive">Supprimé</Badge>;
        } else if (profil.is_init) {
          return <Badge className="bg-green-50 text-green-700 hover:bg-green-100 border-green-200">Actif</Badge>;
        } else {
          return <Badge variant="outline" className="bg-gray-50 text-gray-700 hover:bg-gray-100 border-gray-200">Inactif</Badge>;
        }
      },
    },
    {
      id: "actions",
      header: () => <div className="text-right pr-4">Actions</div>,
      cell: ({ row }) => {
        const profil = row.original;
        return (
          <div className="flex justify-end gap-2 pr-4">
            <Button
              onClick={() => onView(profil.id)}
              size="sm"
              variant="ghost"
              className="h-8 w-8 p-0"
            >
              <ChevronDown className="h-4 w-4" />
            </Button>
            <Button
              onClick={() => onEdit(profil.id)}
              size="sm"
              variant="ghost"
              className="h-8 w-8 p-0"
            >
              <MoreHorizontal className="h-4 w-4" />
            </Button>
            <AlertDialog
              open={profilToDelete === profil.id}
              onOpenChange={(isOpen) => {
                if (isOpen) {
                  setProfilToDelete(profil.id);
                } else {
                  setProfilToDelete(null);
                }
              }}
            >
              <AlertDialogTrigger asChild>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                  disabled={profil.deleted}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M3 6h18"></path>
                    <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                    <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                    <line x1="10" y1="11" x2="10" y2="17"></line>
                    <line x1="14" y1="11" x2="14" y2="17"></line>
                  </svg>
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Êtes-vous sûr ?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Cette action ne peut pas être annulée. Le profil sera marqué comme supprimé dans le système.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Annuler</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleConfirmDelete}
                    className="bg-red-500 hover:bg-red-600"
                  >
                    Supprimer
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        );
      },
    },
  ];

  // Handle error display
  if (error) return <div className="text-red-500">Erreur: {error.message}</div>;

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[200px]">
          <DataTableSearch
            value={searchTerm}
            onChange={onSearchChange}
            placeholder="Rechercher un profil"
          />
        </div>
      </div>
      <div className="rounded-md shadow-sm mt-2 bg-white">
        <DataTable
          serverSidePagination
          columns={columns}
          data={profils}
          isLoading={isLoading}
          renderSearchOutside={true}
          externalGlobalFilter={searchTerm}
          onGlobalFilterChange={onSearchChange}
          pageCount={Math.ceil(total / limit)}
          pageSize={limit}
          totalCount={total}
          onPageChange={onPageChange}
          onPageSizeChange={onLimitChange}
          searchPlaceholder="Rechercher un profil..."
          currentPage={page}
        />
      </div>
    </div>
  );
}

function getApiErrorMessage(error: unknown): string {
  const err = error as {
    response?: { data?: { message?: string | string[] } };
    message?: string;
  };
  const raw = err.response?.data?.message;
  if (Array.isArray(raw)) return raw.join(", ");
  if (typeof raw === "string" && raw.trim().length > 0) return raw.trim();
  if (typeof err.message === "string" && err.message.trim().length > 0) {
    return err.message.trim();
  }
  return "Une erreur est survenue. Veuillez réessayer.";
}
